import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { getBatteryServiceOptions, paymentTypeOptions } from '../../../../../../components/ALSubForm/options'
import { Button } from '../../../../../../components/Button'
import { EvenRouteRow, RouteColumn, RouteRow, RouteWrapper } from '../../../../../../components/layout'
import { NumberInput, SelectInput, TextAreaInput, TextInput } from '../../../../../../components/layout/form/input'
import { FormatMessageType, Maybe, Time, TowingRecordJobStatus, TowingRecordType } from '../../../../../../common/types'
import { Geolocation } from '../../../../../../components/Geolocation/types'
import { AutoliittoDisableFields, GeoLocateAddressData, SubmitFormType } from '../../types'
import { RouteHeader } from '../RouteHeader'
import { AutoliittoSeparatorLine } from '../styled'
import { AutoliittoCoords, repairShopOptions, useRepairShops } from './options'
import { RepairShopModal } from './RepairShopModal'
import { LocationInputMap, toMapData } from '../../../../../../components/layout/form/input/LocationInputMap'
import { ArrivalDateTimeInput } from '../../../../../../components/layout/form/input/DateTimeInput'
import { format } from 'date-fns'
import { RouteNotes } from '../../RouteNotes'
import { FinishRouteAction } from '../../../../../../components/SosSubForm/FinishRoute/types'
import { RoutesFormSelector } from '../../RoutesFormSelector'
import { Images } from '../../../../../../components/images'
import { StyledButton } from '../../../../../../components/ReverseGeocodeButton'
import { ArrivedForm } from './forms/ArrivedForm'
import { FinishedForm } from './forms/FinishedForm'
import { SelectSectionColumn } from '../../../../../TowingOrderPage/AddTowingOrder/TowingOrderDetailsSubFormContents'
import SkeletonLoader, { AlArcCodesForm } from '../../../../../../components/AlArcCodesForm'
import { useItemGroups, useItems } from '../../../../../../components/TowingRecordItems/hooks'
import { useSettings } from '../../../../../Parameters'
import { routeSubformMessages } from '../messages'

interface FinishRouteProps {
  onCancel: () => void // should reset route values
  regNo: string
  setRouteLocation: (addressData: GeoLocateAddressData | null) => void
  updateRouteDate: () => void
  isAutoliitto: boolean
  isSos: boolean
  useExtendedRoutes: boolean
  showAddresInputAs: ShowAddressOptions
  repairShopId?: string | null
  setAutoliittoRepairShopField: (value: string | null) => void
  translatedServiceTypeOptions: object[]
  serviceChannel: string | null
  clear: () => void
  setGeolocatedField: (geolocated: boolean) => void
  initialValues: any
  setLoading: (loading: boolean) => void
  formatMessage: FormatMessageType
  routeDone: boolean
  reportPosition: (location: Geolocation, name: string) => void
  getValue: any
  setValue: (field: string, value: any) => void
  action: FinishRouteAction
  setAction: (action: FinishRouteAction) => void
  jobStatus: TowingRecordJobStatus
  stationId: Maybe<number>
  setSelectedForm: (form: SubmitFormType) => void
  selectedForm: SubmitFormType
  towingRecordId: number
  autoliittoFields: AutoliittoDisableFields
}

export enum ShowAddressOptions {
  DISABLED = 'disabled',
  ADDRESS = 'address',
  REPAIR_SHOP = 'repair_shop',
  SOS_REPAIR_SHOP = 'sos_repair_shop',
}

export const FinishRoute: React.FunctionComponent<FinishRouteProps> = ({
  onCancel,
  regNo,
  setRouteLocation,
  updateRouteDate,
  isAutoliitto,
  isSos,
  useExtendedRoutes,
  showAddresInputAs,
  repairShopId = null,
  setAutoliittoRepairShopField,
  translatedServiceTypeOptions,
  serviceChannel = null,
  clear,
  setLoading,
  routeDone,
  getValue,
  setValue,
  setAction,
  action,
  jobStatus,
  stationId,
  selectedForm,
  setSelectedForm,
  towingRecordId,
}) => {
  const intl = useIntl()
  const [showLocationChange, setShowLocationChange] = useState(false)
  const [showAddressInput, setShowAddressInput] = useState<ShowAddressOptions>(showAddresInputAs)
  const [showImageModal, setShowImageModal] = useState<boolean>(false)

  const settings = useSettings()

  const closeModal = () => {
    setShowLocationChange(false)
  }
 
  const { loading, repairShops } = useRepairShops({skip: !isAutoliitto})

  const { itemGroups } = useItemGroups()
  const { items } = useItems()
  
  const clearLocation = () => {
    setRouteLocation({
      address: '',
      city: '',
      zipcode: '',
      description: '',
      coords: {
        lat: null,
        long: null,
      },
    })
    setAutoliittoRepairShopField(null)
    setShowAddressInput(ShowAddressOptions.ADDRESS)
    closeModal()
  }

  const setLocation = (shopId: string | null) => {
    setLoading(true)
    const repairShop = repairShops.find(shop => shop.id === shopId)

    if (repairShop) {
      const latitude =
        typeof repairShop.location.coordinates === 'object'
          ? (repairShop.location.coordinates as AutoliittoCoords).lat
          : null
      const longitude =
        typeof repairShop.location.coordinates === 'object'
          ? (repairShop.location.coordinates as AutoliittoCoords).lon
          : null
      setRouteLocation({
        address: repairShop.location.address,
        city: repairShop.location.city,
        description: repairShop.name,
        zipcode: repairShop.location.postcode,
        coords: {
          lat: latitude,
          long: longitude,
        },
      })
    }
    setShowAddressInput(ShowAddressOptions.REPAIR_SHOP)
    closeModal()
    setLoading(false)
  }

  const changeServiceType = (serviceType: string | number | undefined) => {
    if (isAutoliitto && serviceType === 'towing') {
      setLocation(repairShopId)
      return
    }
    setShowAddressInput(ShowAddressOptions.DISABLED)
  }

  const label = intl.formatMessage(
    isSos || useExtendedRoutes ? routeSubformMessages.destinationLabel : routeSubformMessages.jobDoneLabel
  )

  const batteryServiceOptions = getBatteryServiceOptions(itemGroups, items)

  useEffect(() => {
    if (routeDone && action === 'edit-finished') {
      setSelectedForm('finished')
    }
  }, [action, routeDone, setSelectedForm])

  const toggleImageModal = () => {
    setShowImageModal(!showImageModal)
  }

  if (showLocationChange && isAutoliitto) {
    return (
      <RepairShopModal
        repairShopId={repairShopId}
        onCancel={closeModal}
        onResetShop={clearLocation}
        onSetShop={setLocation}
        serviceChannel={serviceChannel}
      />
    )
  }

  const arrivedJobStatus = [TowingRecordJobStatus.finished_at_breakdown_location]

  const finishedJobStatus = [
    TowingRecordJobStatus.finished_at_breakdown_location,
    TowingRecordJobStatus.finished_at_destination,
    TowingRecordJobStatus.stopped,
  ]

  const towingRecordType = isSos
    ? TowingRecordType.sos
    : isAutoliitto
    ? TowingRecordType.autoliitto
    : TowingRecordType.default
  const showExtendedRoutes = useExtendedRoutes || isSos

  return (
    <RouteWrapper>
      <RouteHeader label={label} regNo={regNo} cancel={onCancel} toggleImageModal={toggleImageModal} />
      {showImageModal && <Images onClose={toggleImageModal} towingRecordId={towingRecordId} vehicleRegNo={regNo} />}

      {showExtendedRoutes ? (
        <RoutesFormSelector
          action={action}
          setAction={setAction}
          routeDone={routeDone}
          arrived={arrivedJobStatus}
          finished={finishedJobStatus}
          jobStatus={jobStatus}
          selectedForm={selectedForm}
          setSelectedForm={setSelectedForm}
        >
          {selectedForm === 'arrived' && (
            <ArrivedForm
              setValue={setValue}
              getValue={getValue}
              setDateField={updateRouteDate}
              setRouteLocation={setRouteLocation}
              clear={clear}
              action={action}
              setAction={setAction}
              jobStatus={jobStatus}
              stationId={stationId}
              serviceChannel={serviceChannel}
              repairShopId={repairShopId}
              setShowLocationChange={setShowLocationChange}
              showAddressInput={showAddressInput}
              type={towingRecordType}
            />
          )}
          {selectedForm === 'finished' && (
            <FinishedForm
              setValue={setValue}
              getValue={getValue}
              setDateField={updateRouteDate}
              setRouteLocation={setRouteLocation}
              clear={clear}
              action={action}
              setAction={setAction}
              jobStatus={jobStatus}
              stationId={stationId}
              translatedServiceTypeOptions={translatedServiceTypeOptions}
              batteryServiceOptions={batteryServiceOptions}
              changeServiceType={changeServiceType}
              type={towingRecordType}
            />
          )}
        </RoutesFormSelector>
      ) : (
        <>
          <AutoliittoSeparatorLine marginTop="1.5rem" marginBottom="1.5rem" />
          {showAddressInput === ShowAddressOptions.DISABLED && (
            <RouteRow>
              <RouteColumn>
                <TextInput name="disabledAddress" label={intl.formatMessage(routeSubformMessages.fillOnlyCompletionTime)} disabled />
              </RouteColumn>
              <RouteColumn>
                <ArrivalDateTimeInput
                  label={intl.formatMessage(routeSubformMessages.completionTime)}
                  name={`finishRoute`}
                  customOnChange={() => {
                    const value = (format(new Date(), 'HH:mm') as unknown) as Time
                    setValue('finishRoute.arrivalDate', new Date())
                    setValue('finishRoute.arrivalTime', value)
                  }}
                  required={isAutoliitto}
                  showCurrentTimeBtn
                />
              </RouteColumn>
            </RouteRow>
          )}
          {showAddressInput === ShowAddressOptions.ADDRESS && (
            <RouteRow>
              <RouteColumn>
                <LocationInputMap
                  addressLabel={intl.formatMessage(routeSubformMessages.address)}
                  addressName={`finishRoute.address`}
                  cityLabel={intl.formatMessage(routeSubformMessages.city)}
                  cityName={`finishRoute.city`}
                  coordsName={`finishRoute.coords`}
                  zipcodeName={`finishRoute.zipcode`}
                  required={isSos}
                  getValue={getValue}
                  values={toMapData(
                    getValue(`finishRoute.address`),
                    getValue(`finishRoute.city`),
                    getValue(`finishRoute.coords`),
                    getValue(`finishRoute.zipcode`)
                  )}
                  setValue={setValue}
                  setRouteLocation={setRouteLocation}
                  stationId={stationId}
                  clear={clear}
                />
                <SideBySideContainer>
                  {isAutoliitto && (
                    <SelectLocationButton
                      label={intl.formatMessage(routeSubformMessages.setRepairShop)}
                      category="danger"
                      onClick={() => setShowLocationChange(true)}
                    />
                  )}
                </SideBySideContainer>
              </RouteColumn>
              <RouteColumn>
                <ArrivalDateTimeInput
                  label={intl.formatMessage(routeSubformMessages.completionTime)}
                  name={`finishRoute`}
                  customOnChange={() => {
                    const value = (format(new Date(), 'HH:mm') as unknown) as Time
                    setValue('finishRoute.arrivalDate', new Date())
                    setValue('finishRoute.arrivalTime', value)
                  }}
                  required={isAutoliitto}
                  showCurrentTimeBtn
                />
              </RouteColumn>
            </RouteRow>
          )}
          {showAddressInput === ShowAddressOptions.REPAIR_SHOP && (
            <RouteRow>
              <RouteColumn>
                {loading ? (
                  <SkeletonLoader width="100%" height="38px" text={intl.formatMessage(routeSubformMessages.loadingRepairShop)} />
                ) : (
                  <SelectInput
                    label={intl.formatMessage(routeSubformMessages.repairShop)}
                    name="automobileAndTouringClubFinland.repairShopId"
                    options={repairShopOptions(repairShops, serviceChannel, repairShopId)}
                    disabled
                  />
                )}
                <ChangeLocationButton
                  label={intl.formatMessage(routeSubformMessages.changeRepairShop)}
                  category="danger"
                  fontWeight="normal"
                  maxWidth="50%"
                  mobileWidth="100%"
                  onClick={() => setShowLocationChange(true)}
                />
              </RouteColumn>
              <RouteColumn>
                <ArrivalDateTimeInput
                  label={intl.formatMessage(routeSubformMessages.completionTime)}
                  name={`finishRoute`}
                  customOnChange={() => {
                    const value = (format(new Date(), 'HH:mm') as unknown) as Time
                    setValue('finishRoute.arrivalDate', new Date())
                    setValue('finishRoute.arrivalTime', value)
                  }}
                  required={isAutoliitto}
                  showCurrentTimeBtn
                />
              </RouteColumn>
            </RouteRow>
          )}

          {!isSos && !isAutoliitto && settings && (
            <RouteRow columns="1fr 1fr">
              <SelectSectionColumn>
                <SelectInput
                  label={intl.formatMessage(routeSubformMessages.towingReason)}
                  name="towingReason"
                  options={settings.towingReasons.map((label: string) => ({
                    label,
                    value: label,
                  }))}
                />
                <SelectInput
                  label={intl.formatMessage(routeSubformMessages.action)}
                  name="serviceType"
                  onChange={value => {
                    setValue('serviceType', value)

                    if (value !== 'road_service') {
                      setShowAddressInput(ShowAddressOptions.ADDRESS)
                      return
                    }

                    setRouteLocation({
                      address: '',
                      city: '',
                      zipcode: '',
                      description: '',
                      coords: {
                        lat: null,
                        long: null,
                      },
                    })
                    setShowAddressInput(ShowAddressOptions.DISABLED)
                  }}
                  options={translatedServiceTypeOptions}
                />
              </SelectSectionColumn>
            </RouteRow>
          )}
          {isAutoliitto && (
            <>
              <AutoliittoSeparatorLine />
              <EvenRouteRow>
                <AlArcCodesForm serviceType={getValue("serviceType")} />
              </EvenRouteRow>
              <EvenRouteRow>
                <RouteColumn>
                  <SelectInput
                    label={intl.formatMessage(routeSubformMessages.workType)}
                    name="serviceType"
                    options={translatedServiceTypeOptions}
                    onChange={e => {
                      changeServiceType(e)
                    }}
                  />
                </RouteColumn>
                <RouteColumn>
                  <NumberInput
                    label={intl.formatMessage(routeSubformMessages.odometerReading)}
                    name="automobileAndTouringClubFinland.vehicleMileage"
                    required={isAutoliitto}
                    decimals={0}
                  />
                </RouteColumn>
              </EvenRouteRow>
              {getValue('serviceType') === 'battery_service' && (
                <>
                  <EvenRouteRow>
                    <RouteColumn>
                      <SelectInput
                        label={intl.formatMessage(routeSubformMessages.deliveredBattery)}
                        name="automobileAndTouringClubFinland.batteryService.battery"
                        options={batteryServiceOptions}
                        required={isAutoliitto}
                        searchable
                        onChange={e => {
                          if (e !== 'other') {
                            setValue('automobileAndTouringClubFinland.batteryService.deliveredBattery', e)
                          } else {
                            setValue('automobileAndTouringClubFinland.batteryService.deliveredBattery', '')
                          }
                        }}
                      />
                    </RouteColumn>
                    <RouteColumn>
                      <SelectInput
                        label={intl.formatMessage(routeSubformMessages.customerPaymentMethod)}
                        name="automobileAndTouringClubFinland.batteryService.paymentType"
                        options={paymentTypeOptions}
                        required={isAutoliitto}
                      />
                    </RouteColumn>
                  </EvenRouteRow>
                  {getValue('automobileAndTouringClubFinland.batteryService.battery') === 'other' && (
                    <EvenRouteRow>
                      <RouteColumn>
                        <TextInput
                          label={intl.formatMessage(routeSubformMessages.deliveredBatteryOther)}
                          name="automobileAndTouringClubFinland.batteryService.deliveredBattery"
                          required={isAutoliitto}
                        />
                      </RouteColumn>
                    </EvenRouteRow>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      <RouteColumn>
        <TextAreaInput label={intl.formatMessage(routeSubformMessages.additionalInfo)} name="additionalInfo" />
      </RouteColumn>
      <RouteNotes removeLine={isSos || useExtendedRoutes} />
    </RouteWrapper>
  )
}

export const ClearButton = styled(StyledButton as any)`
  margin-top: 1rem;
`

const ChangeLocationButton = styled(Button as any)`
  margin-top: 1rem;
  padding-left: 1rem;
  padding-right: 1rem;
`

const SelectLocationButton = styled(ChangeLocationButton as any)`
  margin-left: 1rem;
`

const SideBySideContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
`
